/**
 * Scheduled sync time (flag `scheduled_sync_time`, MOT-476), client side: the next slot for the
 * time a store is choosing, before it is saved, so the picker can say "Next sync: …". The server's
 * `src/utils/syncTimeSlots.ts` is the rule the scheduler runs on; this mirrors its slot
 * arithmetic (a wall time the clocks skip moves forward by the gap, a repeated one is its first
 * occurrence) and is pinned to the same instants by `syncTimeSlots.test.ts`.
 */

export type SyncTimeInterval = 'hourly' | 'daily' | 'weekly';

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function parseSyncTime(value: unknown): { hour: number; minute: number } | null {
  if (typeof value !== 'string') return null;
  const m = TIME_RE.exec(value.trim());
  return m ? { hour: Number(m[1]), minute: Number(m[2]) } : null;
}

export const SYNC_WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const DEFAULT_SYNC_WEEKDAY = 1;

function wallParts(ms: number, timeZone: string) {
  const out: Record<string, number> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return { ...out, hour: out.hour === 24 ? 0 : out.hour } as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'second', number>;
}

function offsetMs(ms: number, timeZone: string): number {
  const p = wallParts(ms, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
}

/** The instant of a wall time (`wallMs` = that wall time read as if it were UTC) in `timeZone`. */
function wallToInstant(wallMs: number, timeZone: string): number {
  const offsets = Array.from(new Set([-36, 0, 36].map((h) => offsetMs(wallMs + h * 3600_000, timeZone))));
  const valid = offsets.map((o) => wallMs - o).filter((t) => offsetMs(t, timeZone) === wallMs - t);
  return valid.length > 0 ? Math.min(...valid) : Math.max(...offsets.map((o) => wallMs - o));
}

/** Slot instants around `now`, sorted; empty when the time is not a valid `HH:mm`. */
function candidateSlots(
  interval: SyncTimeInterval,
  syncTime: string | null | undefined,
  weekday: number | null | undefined,
  timeZone: string,
  now: Date,
): number[] {
  const time = parseSyncTime(syncTime);
  if (!time) return [];
  const nowMs = now.getTime();
  const local = nowMs + offsetMs(nowMs, timeZone);
  const candidates: number[] = [];
  if (interval === 'hourly') {
    const minuteMs = time.minute * 60_000;
    const base = Math.floor((local - minuteMs) / 3600_000) * 3600_000 + minuteMs;
    for (let k = -3; k <= 3; k++) candidates.push(wallToInstant(base + k * 3600_000, timeZone));
  } else {
    const p = wallParts(nowMs, timeZone);
    const day = Date.UTC(p.year, p.month - 1, p.day);
    const wantDay = typeof weekday === 'number' && weekday >= 0 && weekday <= 6 ? weekday : DEFAULT_SYNC_WEEKDAY;
    for (let k = -8; k <= 8; k++) {
      const date = day + k * 86_400_000;
      if (interval === 'weekly' && new Date(date).getUTCDay() !== wantDay) continue;
      candidates.push(wallToInstant(date + (time.hour * 60 + time.minute) * 60_000, timeZone));
    }
  }
  return candidates.sort((a, b) => a - b);
}

/** The first slot strictly after `now`, or null when the time is not a valid `HH:mm`. */
export function nextSyncSlot(
  interval: SyncTimeInterval,
  syncTime: string | null | undefined,
  weekday: number | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): Date | null {
  const after = candidateSlots(interval, syncTime, weekday, timeZone, now).find((t) => t > now.getTime());
  return after === undefined ? null : new Date(after);
}

/** How long after its slot a run may still start; the server's `SYNC_SLOT_GRACE_MS`. */
export const SYNC_SLOT_GRACE_MS = 60 * 60 * 1000;

export type SyncSlotPreview = { kind: 'due'; slot: Date } | { kind: 'next'; slot: Date };

/**
 * What the scheduler will do with this time, as the server decides it: the latest slot at or
 * before now is still due when it is inside the grace window and later than the last slot the
 * scheduler claimed (`syncSchedule.lastSyncSlotAt`), so saving it starts a run on the next tick;
 * otherwise the next slot.
 */
export function previewSyncSlot(
  interval: SyncTimeInterval,
  syncTime: string | null | undefined,
  weekday: number | null | undefined,
  timeZone: string,
  lastSlotAt: string | null | undefined,
  now: Date = new Date(),
): SyncSlotPreview | null {
  const slots = candidateSlots(interval, syncTime, weekday, timeZone, now);
  const nowMs = now.getTime();
  const latest = slots.filter((t) => t <= nowMs).pop();
  const last = lastSlotAt ? new Date(lastSlotAt).getTime() : NaN;
  if (latest !== undefined && nowMs - latest <= SYNC_SLOT_GRACE_MS && !(last >= latest)) {
    return { kind: 'due', slot: new Date(latest) };
  }
  const after = slots.find((t) => t > nowMs);
  return after === undefined ? null : { kind: 'next', slot: new Date(after) };
}

/** "Central Time (America/Chicago)". */
export function describeTimeZone(timeZone: string): string {
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
    return name && name !== timeZone ? `${name} (${timeZone})` : timeZone;
  } catch {
    return timeZone;
  }
}

/** "Fri, Oct 17, 12:30 PM CDT" in the store's zone. */
export function formatSlot(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(at);
}
